// Where an order point lands (R469).
//
// The season map and the arcade match both give an order by a pick, and the
// two must snap the same way. This leaf holds the three snap bodies. Each
// caller keeps its own armed pick modes and its own accept rule, and it
// calls in here for the point.
//
// Three rules hold for every snap here:
//
//   - **A structure takes the point, and water does not.** A hold, a faction
//     installation, a control point and a deposit are things a player aims a
//     hull AT, and hitting the mark at a low zoom is work the player must not
//     have to do. The snap answers with the centre of the cell the structure
//     stands on. Water has no centre, so a point over water comes back as it
//     was, and `snapped` is false.
//   - **A hit answers before a near miss.** `snapFleetTarget` reads what the
//     pointer landed on first, so a press on a hull is still a press on that
//     hull. The radius catches only a press that missed.
//   - **The radius is a WORLD radius.** The caller divides the screen radius
//     by the camera scale, which is the rule `hit.ts` states. The snap must
//     feel the same at every zoom.
//
// A hull is the one legal target of `snapFleetTarget`, so it never answers
// with a site. `accept` is the rule of the one pick that asks — the escort
// takes an own-faction hull (R784), and the attack takes a hostile one
// (R544). Both surfaces state the same two rules, and this holds one body
// for them, so two picks cannot snap differently.

import { PoiKind, type PointOfInterest, type Site } from '../gen/proto/aquaphobia';
import type { CrosshairMode } from '../render/crosshair-paths';
import type { UIFleet } from '../ui/overlay';
import { cellCenter } from '../world';
import type { FleetPicker, Hit, SitePicker } from './hit';

/** One fleet as the layer describes it, which is what a target pick takes. */
export type FleetTarget = UIFleet & {
  own: boolean; faction: number; classified: boolean;
};

/** The two methods a fleet target snap needs of a fleet store. */
export interface FleetDescriber extends FleetPicker {
  describe(fleetId: number): FleetTarget | null;
}

/**
 * A world point for an order, snapped to a structure that stands near it.
 *
 * `radius` is a world radius. A point over open water comes back as it was,
 * and `snapped` reports which of the two happened.
 */
export function snapWorldPoint(
  sites: SitePicker | null | undefined,
  x: number, y: number, radius: number,
): { wx: number; wy: number; snapped: boolean } {
  const s = sites?.pick(x, y, radius);
  if (!s) return { wx: x, wy: y, snapped: false };
  const [sx, sy] = cellCenter(s.cell);
  return { wx: sx, wy: sy, snapped: true };
}

/**
 * The hull a target pick takes, snapped.
 *
 * `hit` is what the pointer landed on. It answers first, so a press on a
 * hull is still a press on that hull; the wider radius catches a near miss
 * alone. A hull the `accept` rule refuses is not a target, and the near miss
 * gets the next try.
 */
export function snapFleetTarget(
  fleets: FleetDescriber,
  hit: Hit | null,
  x: number, y: number, radius: number,
  accept: (t: FleetTarget) => boolean,
): FleetTarget | undefined {
  const ok = (id: number) => {
    const t = fleets.describe(id);
    return t && accept(t) ? t : undefined;
  };
  if (hit?.kind === 'fleet') {
    const t = ok(hit.id);
    if (t) return t;
  }
  const near = fleets.pick(x, y, radius);
  return near ? ok(near.id) : undefined;
}

/**
 * The deposit a gather pick takes at a world point, or null.
 *
 * A deposit is the one legal target of the gather mission, so the store
 * filters every other site kind out.
 */
export function snapDepositAt(
  sites: SitePicker | null | undefined,
  x: number, y: number, radius: number,
): Site | null {
  return sites?.pickDeposit(x, y, radius) ?? null;
}

/**
 * The cell of the vent a gather pick takes at a world point, or -1 (R3147).
 *
 * A vent is a gather field as a deposit is, so a gather pick, a context
 * order of a Dredger and SEND HAULER take it the same way. The vents are
 * the points of the survey; the pick takes the nearest vent whose cell
 * centre is within `radius`, and the first in id order on a tie.
 */
export function pickVent(
  pois: readonly Pick<PointOfInterest, 'kind' | 'cell'>[] | null | undefined,
  x: number, y: number, radius: number,
): number {
  let best = -1, bestD = radius * radius;
  for (const p of pois ?? []) {
    if (p.kind !== PoiKind.POI_KIND_VENT) continue;
    const [cx, cy] = cellCenter(p.cell);
    const d = (cx - x) * (cx - x) + (cy - y) * (cy - y);
    if (d <= bestD && (best < 0 || d < bestD)) { best = p.cell; bestD = d; }
  }
  return best;
}

/**
 * The cell of the gather field a gather pick takes at a world point, or
 * -1: a deposit first, then a vent (R3147). No vent stands on a deposit,
 * so the order of the two tests changes no pick.
 */
export function snapGatherAt(
  sites: SitePicker | null | undefined,
  pois: readonly Pick<PointOfInterest, 'kind' | 'cell'>[] | null | undefined,
  x: number, y: number, radius: number,
): number {
  const dep = snapDepositAt(sites, x, y, radius);
  return dep ? dep.cell : pickVent(pois, x, y, radius);
}

/**
 * The picks of one surface at a screen point. The season map's `Commands`
 * and the arcade match's `ArcadeInput` both answer them, and `snapsAt`
 * asks them.
 */
export interface SnapSurface {
  hitTest(cx: number, cy: number): Hit | null;
  snapPoint(cx: number, cy: number): { snapped: boolean };
  /** The cell of the gather field a gather pick takes, a deposit or a
   *  vent, or -1 (R3147). */
  snapGather(cx: number, cy: number): number;
  /** The own-faction hull an escort pick takes; `self` is never taken. */
  snapEscortTarget(cx: number, cy: number, hit: Hit | null, self: number): FleetTarget | undefined;
  /** The hostile hull an attack pick takes (R544). */
  snapAttackTarget(cx: number, cy: number, hit: Hit | null): FleetTarget | undefined;
  /** The fleet the escort pick is armed for, which it must not escort. */
  escortSelf(): number;
}

/**
 * Would this mode's pick snap at this screen point? (R469)
 *
 * The mark wears its snap ring on a yes, so the ring must not promise a
 * snap where the pick takes the raw point. Only `move`, `gather`, `escort`
 * and `attack` snap at all. The attack snaps to a hostile hull alone
 * (R544): over open water it places a point, so it answers no there.
 * Patrol takes a place on the water, and neither aim ever snaps. Each
 * surface's order paths are the mirror of this table, and the two must be
 * read together.
 *
 * It is hit-test work, so a page runs it when the answer can have changed
 * and not once a frame (ui-map-interface.md §10.3).
 */
export function snapsAt(s: SnapSurface, mode: CrosshairMode, cx: number, cy: number): boolean {
  if (mode === 'move') return s.snapPoint(cx, cy).snapped;
  if (mode === 'gather') return s.snapGather(cx, cy) >= 0;
  if (mode === 'escort') return !!s.snapEscortTarget(cx, cy, s.hitTest(cx, cy), s.escortSelf());
  if (mode === 'attack') return !!s.snapAttackTarget(cx, cy, s.hitTest(cx, cy));
  return false;
}
